import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Save, Edit2, Eye, BookOpen, Image, Video, FileText, ChevronDown, ChevronUp, 
  Upload, X, Loader2, Check, Plus, Trash2, Link, ExternalLink, GripVertical
} from 'lucide-react';
import { cmsAPI, filesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminCMS = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const res = await cmsAPI.getSections();
      setSections(res.data);
    } catch (error) {
      console.error('Error loading sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (sectionId, updates) => {
    try {
      await cmsAPI.updateSection(sectionId, updates);
      toast({ title: 'Section mise à jour', description: 'Les modifications ont été enregistrées.' });
      setEditingSection(null);
      loadSections();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    }
  };

  const toggleExpand = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const resetCMS = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser tout le contenu CMS ?')) return;
    try {
      await cmsAPI.reset();
      toast({ title: 'CMS réinitialisé' });
      loadSections();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const groupedSections = sections.reduce((acc, section) => {
    if (!acc[section.page]) acc[section.page] = [];
    acc[section.page].push(section);
    return acc;
  }, {});

  const pageNames = {
    accueil: '🏠 Page d\'accueil',
    kiddoes: '👶 Kiddoes (4-6 ans)',
    enfants: '🧒 Enfants (6-12 ans)',
    adolescents: '👦 Adolescents (10-18 ans)',
    adultes: '👨‍💼 Adultes & Entreprises',
    tarifs: '💰 Tarifs',
    contact: '📞 Contact',
    'mentions-legales': '⚖️ Mentions légales',
    cgv: '📋 CGV',
    'form-choix': '📝 Formulaire - Page de choix',
    'form-enfant': '📝 Formulaire - Inscription Enfant',
    'form-adulte': '📝 Formulaire - Inscription Adulte',
    'form-entreprise': '📝 Formulaire - Devis Entreprise',
    home: '🏠 Page d\'accueil (ancien)',
    global: '🌐 Éléments globaux'
  };

  const pageOrder = ['accueil', 'kiddoes', 'enfants', 'adolescents', 'adultes', 'tarifs', 'contact', 'form-choix', 'form-enfant', 'form-adulte', 'form-entreprise', 'mentions-legales', 'cgv', 'home', 'global'];
  const sortedPages = Object.keys(groupedSections).sort((a, b) => {
    const orderA = pageOrder.indexOf(a);
    const orderB = pageOrder.indexOf(b);
    return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-cms">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Gestion du contenu (CMS)</h1>
            <p className="text-sm text-[#6777A1] mt-1">Modifiez le contenu des pages de votre site</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetCMS} className="text-red-600 hover:bg-red-50">
              Réinitialiser
            </Button>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 bg-[#285BBB] text-white rounded-lg hover:bg-[#2C3C65]"
            >
              <Eye className="h-4 w-4 mr-2" />
              Voir le site
            </a>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800">
            <strong>💡 Éditeur CMS complet :</strong> Modifiez les textes, ajoutez des images, des vidéos et des boutons avec liens personnalisés.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
          </div>
        ) : sections.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <BookOpen className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <p className="text-[#6777A1] mb-4">Aucune section CMS trouvée</p>
            <Button onClick={resetCMS} className="bg-[#285BBB]">Initialiser le contenu</Button>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedPages.map((page) => (
              <div key={page} className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-[#2C3C65] to-[#285BBB] text-white px-6 py-4">
                  <h2 className="text-lg font-semibold">{pageNames[page] || page}</h2>
                  <p className="text-sm text-blue-100 opacity-80">{groupedSections[page].length} section(s)</p>
                </div>
                <div className="divide-y">
                  {groupedSections[page].map((section) => (
                    <div key={section.id} className="p-6">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleExpand(section.id)}
                      >
                        <div className="flex items-center space-x-3">
                          <BookOpen className="h-5 w-5 text-[#285BBB]" />
                          <div>
                            <h3 className="font-medium text-[#2C3C65]">{section.section_key}</h3>
                            <p className="text-sm text-[#6777A1]">{section.title || '(Sans titre)'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {section.image_url && <Image className="h-4 w-4 text-green-500" />}
                          {section.video_url && <Video className="h-4 w-4 text-red-500" />}
                          {section.buttons?.length > 0 && <Link className="h-4 w-4 text-blue-500" />}
                          {!section.is_visible && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">Masqué</span>
                          )}
                          {expandedSections[section.id] ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {expandedSections[section.id] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-6"
                        >
                          {editingSection === section.id ? (
                            <SectionEditor
                              section={section}
                              onSave={(updates) => handleSave(section.id, updates)}
                              onCancel={() => setEditingSection(null)}
                            />
                          ) : (
                            <SectionPreview 
                              section={section} 
                              onEdit={() => setEditingSection(section.id)} 
                            />
                          )}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Prévisualisation d'une section
const SectionPreview = ({ section, onEdit }) => (
  <div className="space-y-4">
    {/* Textes */}
    <div className="grid md:grid-cols-2 gap-4">
      <div className="space-y-1">
        <p className="text-xs text-[#6777A1] uppercase tracking-wide">Titre</p>
        <p className="text-[#2C3C65] font-medium">{section.title || '-'}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs text-[#6777A1] uppercase tracking-wide">Sous-titre</p>
        <p className="text-[#2C3C65]">{section.subtitle || '-'}</p>
      </div>
    </div>
    
    {section.content && (
      <div className="space-y-1">
        <p className="text-xs text-[#6777A1] uppercase tracking-wide">Contenu</p>
        <p className="text-[#2C3C65] text-sm bg-gray-50 p-3 rounded-lg">{section.content}</p>
      </div>
    )}

    {/* Images */}
    {(section.image_url || section.images?.length > 0) && (
      <div className="space-y-2">
        <p className="text-xs text-[#6777A1] uppercase tracking-wide flex items-center gap-1">
          <Image className="h-3 w-3" /> Images
        </p>
        <div className="flex flex-wrap gap-2">
          {section.image_url && (
            <img src={section.image_url} alt="" className="h-20 w-auto rounded-lg border object-cover" />
          )}
          {section.images?.map((img, i) => (
            <img key={i} src={img.url} alt={img.alt || ''} className="h-20 w-auto rounded-lg border object-cover" />
          ))}
        </div>
      </div>
    )}

    {/* Vidéos */}
    {(section.video_url || section.videos?.length > 0) && (
      <div className="space-y-2">
        <p className="text-xs text-[#6777A1] uppercase tracking-wide flex items-center gap-1">
          <Video className="h-3 w-3" /> Vidéos
        </p>
        <div className="flex flex-wrap gap-2">
          {section.video_url && (
            <a href={section.video_url} target="_blank" rel="noopener noreferrer" 
               className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100">
              <Video className="h-4 w-4" />
              Vidéo principale
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {section.videos?.map((vid, i) => (
            <a key={i} href={vid.url} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100">
              <Video className="h-4 w-4" />
              {vid.title || `Vidéo ${i + 1}`}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      </div>
    )}

    {/* Boutons */}
    {section.buttons?.length > 0 && (
      <div className="space-y-2">
        <p className="text-xs text-[#6777A1] uppercase tracking-wide flex items-center gap-1">
          <Link className="h-3 w-3" /> Boutons / Liens
        </p>
        <div className="flex flex-wrap gap-2">
          {section.buttons.map((btn, i) => (
            <a key={i} href={btn.url} target="_blank" rel="noopener noreferrer"
               className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${
                 btn.style === 'primary' ? 'bg-[#285BBB] text-white' :
                 btn.style === 'secondary' ? 'bg-gray-100 text-gray-700' :
                 'border border-[#285BBB] text-[#285BBB]'
               }`}>
              {btn.text}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      </div>
    )}

    <Button onClick={onEdit} className="mt-4 bg-[#285BBB] hover:bg-[#1e4a9a] text-white">
      <Edit2 className="h-4 w-4 mr-2" />
      Modifier cette section
    </Button>
  </div>
);

// Éditeur de section complet
const SectionEditor = ({ section, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: section.title || '',
    subtitle: section.subtitle || '',
    content: section.content || '',
    image_url: section.image_url || '',
    video_url: section.video_url || '',
    images: section.images || [],
    videos: section.videos || [],
    buttons: section.buttons || [],
    is_visible: section.is_visible !== false,
    meta_title: section.meta_title || '',
    meta_description: section.meta_description || '',
    meta_keywords: section.meta_keywords || '',
  });
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleImageUpload = async (e, field = 'image_url') => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Erreur', description: 'Veuillez sélectionner une image', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Erreur', description: 'L\'image doit faire moins de 5MB', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const res = await filesAPI.upload(file);
      const imageUrl = filesAPI.getUrl(res.data.id);
      
      if (field === 'image_url') {
        setFormData({ ...formData, image_url: imageUrl });
      } else {
        setFormData({
          ...formData,
          images: [...formData.images, { url: imageUrl, alt: file.name }]
        });
      }
      toast({ title: 'Image uploadée' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'uploader', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    if (index === -1) {
      setFormData({ ...formData, image_url: '' });
    } else {
      setFormData({
        ...formData,
        images: formData.images.filter((_, i) => i !== index)
      });
    }
  };

  const addVideo = () => {
    setFormData({
      ...formData,
      videos: [...formData.videos, { url: '', title: '' }]
    });
  };

  const updateVideo = (index, field, value) => {
    const newVideos = [...formData.videos];
    newVideos[index][field] = value;
    setFormData({ ...formData, videos: newVideos });
  };

  const removeVideo = (index) => {
    setFormData({
      ...formData,
      videos: formData.videos.filter((_, i) => i !== index)
    });
  };

  const addButton = () => {
    setFormData({
      ...formData,
      buttons: [...formData.buttons, { text: 'Nouveau bouton', url: '/', style: 'primary' }]
    });
  };

  const updateButton = (index, field, value) => {
    const newButtons = [...formData.buttons];
    newButtons[index][field] = value;
    setFormData({ ...formData, buttons: newButtons });
  };

  const removeButton = (index) => {
    setFormData({
      ...formData,
      buttons: formData.buttons.filter((_, i) => i !== index)
    });
  };

  const tabs = [
    { id: 'content', label: 'Contenu', icon: FileText },
    { id: 'media', label: 'Médias', icon: Image },
    { id: 'buttons', label: 'Boutons', icon: Link },
    { id: 'seo', label: 'SEO', icon: BookOpen },
  ];

  return (
    <div className="bg-[#F5F7FF] rounded-xl p-6">
      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-[#285BBB] text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Titre</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Titre de la section"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Sous-titre</label>
              <Input
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder="Sous-titre (optionnel)"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2C3C65] mb-1">Contenu</label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={5}
              placeholder="Texte de la section..."
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_visible"
              checked={formData.is_visible}
              onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
              className="h-4 w-4"
            />
            <label htmlFor="is_visible" className="text-sm text-[#2C3C65]">Section visible sur le site</label>
          </div>
        </div>
      )}

      {/* Media Tab */}
      {activeTab === 'media' && (
        <div className="space-y-6">
          {/* Image principale */}
          <div>
            <label className="block text-sm font-medium text-[#2C3C65] mb-2">Image principale</label>
            {formData.image_url ? (
              <div className="relative inline-block">
                <img src={formData.image_url} alt="" className="h-32 w-auto rounded-lg border" />
                <button
                  onClick={() => removeImage(-1)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-4">
                <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#285BBB] hover:bg-blue-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'image_url')}
                    className="hidden"
                  />
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5 text-[#285BBB]" />}
                  <span className="text-[#285BBB]">Uploader une image</span>
                </label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="ou coller l'URL de l'image"
                  className="flex-1"
                />
              </div>
            )}
          </div>

          {/* Images supplémentaires */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#2C3C65]">Images supplémentaires</label>
              <label className="flex items-center gap-1 px-3 py-1 bg-[#285BBB] text-white rounded-lg cursor-pointer hover:bg-[#2C3C65] text-sm">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'images')}
                  className="hidden"
                />
                <Plus className="h-4 w-4" />
                Ajouter
              </label>
            </div>
            {formData.images.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative">
                    <img src={img.url} alt={img.alt} className="h-24 w-auto rounded-lg border" />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Aucune image supplémentaire</p>
            )}
          </div>

          {/* Vidéo principale */}
          <div>
            <label className="block text-sm font-medium text-[#2C3C65] mb-2">Vidéo YouTube principale</label>
            <Input
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {formData.video_url && formData.video_url.includes('youtube') && (
              <div className="mt-2 aspect-video max-w-sm rounded-lg overflow-hidden">
                <iframe
                  src={formData.video_url.replace('watch?v=', 'embed/').split('&')[0]}
                  className="w-full h-full"
                  allowFullScreen
                  title="Aperçu vidéo"
                />
              </div>
            )}
          </div>

          {/* Vidéos supplémentaires */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#2C3C65]">Vidéos supplémentaires</label>
              <Button size="sm" onClick={addVideo} className="bg-[#285BBB]">
                <Plus className="h-4 w-4 mr-1" />
                Ajouter
              </Button>
            </div>
            {formData.videos.length > 0 ? (
              <div className="space-y-2">
                {formData.videos.map((vid, index) => (
                  <div key={index} className="flex gap-2 items-center bg-white p-3 rounded-lg">
                    <Input
                      value={vid.title}
                      onChange={(e) => updateVideo(index, 'title', e.target.value)}
                      placeholder="Titre de la vidéo"
                      className="w-40"
                    />
                    <Input
                      value={vid.url}
                      onChange={(e) => updateVideo(index, 'url', e.target.value)}
                      placeholder="URL YouTube"
                      className="flex-1"
                    />
                    <button onClick={() => removeVideo(index)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Aucune vidéo supplémentaire</p>
            )}
          </div>
        </div>
      )}

      {/* Buttons Tab */}
      {activeTab === 'buttons' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#6777A1]">Ajoutez des boutons avec des liens personnalisés</p>
            <Button onClick={addButton} className="bg-[#285BBB]">
              <Plus className="h-4 w-4 mr-1" />
              Ajouter un bouton
            </Button>
          </div>

          {formData.buttons.length > 0 ? (
            <div className="space-y-3">
              {formData.buttons.map((btn, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Texte du bouton</label>
                      <Input
                        value={btn.text}
                        onChange={(e) => updateButton(index, 'text', e.target.value)}
                        placeholder="En savoir plus"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Lien (URL)</label>
                      <Input
                        value={btn.url}
                        onChange={(e) => updateButton(index, 'url', e.target.value)}
                        placeholder="/contact ou https://..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Style</label>
                      <select
                        value={btn.style}
                        onChange={(e) => updateButton(index, 'style', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="primary">Principal (bleu plein)</option>
                        <option value="secondary">Secondaire (gris)</option>
                        <option value="outline">Contour (bordure bleue)</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Aperçu :</span>
                      <span className={`px-3 py-1 rounded-lg text-sm ${
                        btn.style === 'primary' ? 'bg-[#285BBB] text-white' :
                        btn.style === 'secondary' ? 'bg-gray-100 text-gray-700' :
                        'border border-[#285BBB] text-[#285BBB]'
                      }`}>
                        {btn.text || 'Bouton'}
                      </span>
                    </div>
                    <button onClick={() => removeButton(index)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white p-8 rounded-lg text-center">
              <Link className="h-12 w-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500">Aucun bouton configuré</p>
              <p className="text-sm text-gray-400">Cliquez sur "Ajouter un bouton" pour créer des liens</p>
            </div>
          )}
        </div>
      )}

      {/* SEO Tab */}
      {activeTab === 'seo' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#2C3C65] mb-1">Meta Title (titre pour Google)</label>
            <Input
              value={formData.meta_title}
              onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
              placeholder="Titre pour les moteurs de recherche"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2C3C65] mb-1">Meta Description</label>
            <Textarea
              value={formData.meta_description}
              onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
              rows={2}
              placeholder="Description pour les moteurs de recherche (150-160 caractères)"
            />
            <p className="text-xs text-gray-400 mt-1">{formData.meta_description.length}/160 caractères</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2C3C65] mb-1">Mots-clés</label>
            <Input
              value={formData.meta_keywords}
              onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
              placeholder="mot-clé1, mot-clé2, mot-clé3"
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={() => onSave(formData)} className="bg-[#285BBB] hover:bg-[#2C3C65]">
          <Save className="h-4 w-4 mr-2" />
          Enregistrer
        </Button>
      </div>
    </div>
  );
};

export default AdminCMS;
